import {
  ProviderUnavailableError,
  type ProviderCallOptions,
  type ProviderJobHandle,
  type ProviderVideoInpaintJobState,
  type VideoInpaintProvider,
  type VideoInpaintRequest,
  type VideoInpaintResult,
} from "@loumy/contracts";
import { safeFetchBytes } from "./safe-fetch";

// §7 `video-inpaint`-Provider → **Wan 2.2 VACE Fun A14B** über die fal.ai-Queue-API
// (D131, Modell amendiert per D134): maskenbasiertes Video-Inpainting für die Loop-Naht des
// Spritesheet-Tools. Der loop-Knoten (Trim-Modus) baut eine 81-Frame-Kondition
// (Kontext-Frames + neutralgraue Lücke) + ein frame-synchrones Masken-Video
// (schwarz=erhalten, weiß=generieren) + ein Referenzbild als Identitäts-Anker (D135:
// der MITTEL-Frame — frame[0] liegt im Replace-Fenster; D131-Alt-Specs: frame[0]);
// VACE generiert die maskierten Frames mit echtem Bewegungs-Prior. Gleicher FAL_API_KEY
// wie video/image/vision/image-edit/rife (EIN Vertrag).
//
// Modell-Contract `fal-ai/wan-22-vace-fun-a14b/inpainting` (fal-Doku verifiziert 2026-06-07).
// D134-Amendment: das ursprüngliche D131-Modell `fal-ai/wan-vace-1-3b` wurde von fal
// ZURÜCKGEZOGEN (Live-Smoke #2: submit-404 „Application not found"; die Doku existierte am
// 2026-06-06 noch) — Ersatz per User-Entscheid („das neueste/beste"): Wan 2.2 VACE Fun A14B
// (Alibaba-PAI-VACE-Finetune auf der neueren Wan-2.2-Basis; Original-VACE endet bei Wan 2.1).
// Schema FELD-IDENTISCH zu unserem Submit (gegen die /inpainting-API-Seite verifiziert):
//   prompt (REQUIRED), video_url (Quell-/Konditions-Video), mask_video_url (Masken-Video),
//   ref_image_urls (Liste), frames_per_second (wir: 24, D131-Tempo), resolution ∈
//   auto|240p|360p|480p|580p|720p (wir senden weiter nur 480p|580p|720p), seed, num_frames
//   (Default 81 — wir senden es NICHT: mit video_url+mask folgt die Output-Länge der Quelle,
//   unsere Kondition IST exakt VACE_CONDITION_FRAMES Frames; ops.py-SSOT). Output: { video: { url }, seed }.
//   COGS ≈ $0.05/Video-Sekunde @480p (16-fps-Normierung) ⇒ ~$0.25/Naht @81 Frames — §13-Pricing.
//   EXPERIMENT (2026-06-08, befristet, PRD D135): VACE_CONDITION_FRAMES TESTWEISE = 21 (4n+1)
//   ⇒ ~$0.06/Naht; OUT-OF-DISTRIBUTION, Naht-Qualität UNVERIFIZIERT. Default-Rückkehr 81.
//
// D27 (umgestellt vom synchronen Einzelaufruf): generate() submittet NUR → Handle (status_url/
// response_url in raw, DB-persistiert); poll() ist EIN schneller Status-GET (KEIN SSE-Stream —
// der Runner-Treiber muss nach dem Tick-Budget pausieren können). Erschöpft das Pro-Tick-Budget
// den Poll, pollt ein Resume-Tick dasselbe Handle weiter (kein Re-Submit). Das löst strukturell
// die volle fal-Queue: kein 780-s-Job-Exec-Timeout/CANCELED mehr, keine Deploy-Eviction-Sensibilität,
// keine doppelten fal-COGS. Der frühere SSE-Stream + 5-s-Poll-Fallback (D106, gegen das
// Free-Plan-Subrequest-Limit in EINEM synchronen Tick) entfällt: jeder Resume-Tick ist eine
// neue Invocation mit eigenem Subrequest-Budget, ein Poll = 1 Subrequest. `opts.signal` bricht ab.

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_VACE_MODEL = "fal-ai/wan-22-vace-fun-a14b/inpainting";
// Tempo-Hebel (User-Direktive „schneller & günstiger" nach dem 600-s-CANCELED-Befund):
// Endpoint-Default ist 30 — 20 Steps rechnen ~⅓ schneller bei gleichem Preis (fal billt
// per Videosekunde) und für 10 Greenscreen-Naht-Frames qualitativ unkritisch. Ein
// SCHNELLERES VACE-Modell existiert auf fal nicht mehr (1.3B zurückgezogen, D134(g);
// wan-vace-14b = gleiche Größenklasse). §13-kalibrierbar.
const NUM_INFERENCE_STEPS = 20;
// Größen-Cap des Ergebnis-MP4 (DoS-/Speicher-Schutz): 81 Frames @720p sind real einstellige MB.
const MAX_VIDEO_BYTES = 64 * 1024 * 1024;
// fal-Bounds (Doku): frames_per_second 5–24.
const MIN_FPS = 5;
const MAX_FPS = 24;
const RESOLUTIONS = new Set(["480p", "580p", "720p"]);
// D136: vom Endpoint unterstützte Ziel-Seitenverhältnisse — die Kondition kommt GPU-seitig
// bereits exakt auf eines davon gepolstert; explizites Mitsenden verhindert, dass 'auto'
// ein anderes Bucket wählt und die Geometrie verzerrt (Naht-Streckungs-Befund).
const ASPECT_RATIOS = new Set(["16:9", "1:1", "9:16"]);

/**
 * poll()-intern (Codex-P1): ein TRANSIENTER Fehler beim Ergebnis-Download NACH `COMPLETED`
 * (fal-Result-GET oder CDN 429/5xx). Der Provider-Job IST fertig, nur der Last-Mile-Download
 * flackerte — poll() meldet dann `processing` (Retry im nächsten Poll/Tick) statt den fertigen
 * Job terminal zu killen. Terminale Fehler (4xx, Nicht-MP4, Host-Mismatch) bleiben Hard-Fail.
 */
class VaceDownloadTransientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "VaceDownloadTransientError";
  }
}

export interface FalVaceConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/wan-22-vace-fun-a14b/inpainting`, D134-Amendment). */
  model?: string;
}

/**
 * §7 video-inpaint → Wan-VACE über fal.ai (D131, D27-Handle). `generate({videoUrl, maskVideoUrl,
 * refImageUrl, prompt, fps, resolution, seed})` → submit → Handle (status_url/response_url);
 * `poll(handle)` → ein Status-GET, bei COMPLETED Ergebnis-MP4 laden (safeFetchBytes,
 * größen-gedeckelt) → Bytes. Der Resume-Treiber (runVideoInpaintProviderJob) pausiert/pollt.
 */
export class FalVaceProvider implements VideoInpaintProvider {
  readonly id = "fal-vace";
  readonly version = 1;
  readonly displayName = "fal.ai (Wan 2.2 VACE Fun A14B Video-Inpainting)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalVaceConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_VACE_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("video-inpaint", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie die übrigen fal-Adapter). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal vace: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal vace: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal vace: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /**
   * Ergebnis-URL gegen die fal-CDN-Hosts pinnen (D131-Codex-P1): das MP4 geht base64-inline
   * an die GPU-Runtime und wird dort blind decodiert — die Quelle MUSS fal sein (fal.media/
   * fal.run/fal.ai inkl. Subdomains), kein beliebiger Provider-Response-Host.
   */
  private requireFalCdnUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal vace: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).hostname.toLowerCase().replace(/\.+$/, "");
    } catch {
      throw new Error(`fal vace: '${field}' ist keine gültige URL.`);
    }
    const allowed = ["fal.media", "fal.run", "fal.ai"].some((d) => host === d || host.endsWith(`.${d}`));
    if (!allowed) throw new Error(`fal vace: '${field}'-Host '${host}' liegt nicht auf einem fal-CDN-Host.`);
    return url;
  }

  /**
   * COMPLETED → Ergebnis-MP4 laden. Transiente Download-Flaps (Result-GET ODER CDN 429/5xx)
   * werden als `VaceDownloadTransientError` geworfen (poll() → 'processing', Retry) — terminale
   * 4xx / Nicht-MP4 / Host-Mismatch bleiben harte Fehler (Codex-P1).
   */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<VideoInpaintResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      // 429/5xx = transienter Flap nach fertigem Job → Retry (poll meldet 'processing').
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new VaceDownloadTransientError(`fal vace result: HTTP ${resultRes.status} ${resultRes.statusText} (transient)`);
      // D119-Diagnose-Muster: terminale 4xx tragen den (gekürzten) Body in der Meldung —
      // ein Endpoint-Reject (Frame-Raster/Parameter-Kombi) muss in jobs.error lesbar sein.
      const body = (await resultRes.text().catch(() => "")).slice(0, 500);
      throw new Error(`fal vace result: HTTP ${resultRes.status} ${resultRes.statusText}${body ? ` — ${body}` : ""}`);
    }
    const result = (await resultRes.json()) as { video?: { url?: unknown }; seed?: unknown };
    // D131-Codex-P1: Ergebnis-URL host-gepinnt (fal-CDN) + content-type-geprüft + MP4-Magic —
    // die Bytes gehen base64-inline an die GPU-Runtime und werden dort blind ffmpeg-decodiert.
    const url = this.requireFalCdnUrl(result.video?.url, "video.url");
    let bytes: Uint8Array;
    try {
      ({ bytes } = await safeFetchBytes(url, { maxBytes: MAX_VIDEO_BYTES, signal, expectContentType: "video/" }));
    } catch (e) {
      // CDN 429/5xx (safe-fetch hängt den Status ans Error-Objekt) = transient → Retry.
      const status = (e as { status?: number }).status;
      if (status === 429 || (typeof status === "number" && status >= 500))
        throw new VaceDownloadTransientError(`fal vace: Ergebnis-Download HTTP ${status} (transient)`);
      throw e; // content-type/Größe/Redirect/4xx/Abort → terminal
    }
    // MP4/ISO-BMFF-Magic ('ftyp' bei Offset 4) — fängt HTML-Fehlerseiten/Provider-Drift ab.
    if (bytes.length < 12 || bytes[4] !== 0x66 || bytes[5] !== 0x74 || bytes[6] !== 0x79 || bytes[7] !== 0x70) {
      throw new Error("fal vace: Ergebnis ist kein MP4 (ftyp-Magic fehlt — Provider-Drift?).");
    }
    return {
      videoBytes: bytes,
      provenance: {
        provider: this.id,
        modelVersion: this.model,
        requestId,
        seed: typeof result.seed === "number" && Number.isFinite(result.seed) ? result.seed : null,
      },
    };
  }

  /**
   * D27: NUR submitten → Handle (status_url/response_url in `raw`, DB-persistiert). KEIN Poll/
   * Download hier — der Treiber persistiert das Handle VOR dem ersten poll(), damit ein
   * Resume-Tick weiterpollen kann statt neu zu submitten (kein verlorener fal-Job, keine
   * doppelten COGS). `opts.signal` bricht den Submit ab.
   */
  async generate(req: VideoInpaintRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const fps = Math.max(MIN_FPS, Math.min(MAX_FPS, Math.round(Number.isFinite(req.fps) ? req.fps : MAX_FPS)));
    const resolution = req.resolution !== undefined && RESOLUTIONS.has(req.resolution) ? req.resolution : "480p";

    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        prompt: req.prompt,
        video_url: req.videoUrl,
        mask_video_url: req.maskVideoUrl,
        ...(req.refImageUrl ? { ref_image_urls: [req.refImageUrl] } : {}),
        frames_per_second: fps,
        resolution,
        num_inference_steps: NUM_INFERENCE_STEPS,
        // D153: Safety-Checker für die INTERNE Naht-Operation abschalten (fal-Default = true).
        // ref_image_urls/video_url sind Frames aus dem bereits an der gen-Stufe (Pixverse,
        // eigener Filter) geprüften Output — eine zweite NSFW-Prüfung ist redundant und
        // produzierte reproduzierbar False-Positives (HTTP 422 "ref_image_urls contain NSFW
        // content" auf legitimen, voll bekleideten/stylized Motiven). Der EINGANGS-Filter am
        // User-Upload (normalize/gen) bleibt unberührt; abgeschaltet wird nur die redundante
        // Doppelprüfung selbst-generierter Zwischen-Frames.
        enable_safety_checker: false,
        // D154 (Voll-Modus): den Output an die Konditions-Frame-Anzahl binden (fal-Default
        // num_frames=81 würde sonst die 1:1-Ersetzung sprengen — gestreckte Bewegung). Nur
        // gesetzt, wenn der Aufrufer es explizit verlangt (Fenster-Modus sendet es nie).
        ...(req.matchInputNumFrames === true ? { match_input_num_frames: true } : {}),
        // D136: nur bekannte Buckets durchreichen — unbekannte Werte fallen auf 'auto'
        // (Endpoint-Default) zurück statt einen 422 zu riskieren.
        ...(req.aspectRatio !== undefined && ASPECT_RATIOS.has(req.aspectRatio) ? { aspect_ratio: req.aspectRatio } : {}),
        ...(req.seed !== undefined && Number.isFinite(req.seed) ? { seed: Math.trunc(req.seed) } : {}),
      }),
      signal,
    });
    if (!submit.ok) {
      const body = submit.status < 500 ? (await submit.text().catch(() => "")).slice(0, 500) : "";
      throw new Error(`fal vace submit: HTTP ${submit.status} ${submit.statusText}${body ? ` — ${body}` : ""}`);
    }
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal vace submit: keine request_id in der Antwort.");
    // status_url/response_url hier gegen den fal-Host pinnen — sie gehen ins persistierte Handle.
    const statusUrl = this.requireFalUrl(sub.status_url, "status_url");
    const responseUrl = this.requireFalUrl(sub.response_url, "response_url");

    return {
      providerId: this.id,
      providerVersion: this.version,
      externalId: sub.request_id,
      // Poll-Koordinaten für poll() (DB-jsonb-persistiert, Resume-stabil). Werden in poll()
      // ERNEUT gegen den fal-Host validiert (das Handle kommt beim Resume aus der DB).
      raw: { statusUrl, responseUrl },
    };
  }

  /**
   * D27: EIN schneller Status-GET (kein blockierender Stream — der Tick-Treiber muss pausieren
   * können). `IN_QUEUE`→pending, `IN_PROGRESS`→processing, `COMPLETED`→Ergebnis-MP4 laden →done.
   * Transiente HTTP-Fehler (429/5xx) → processing (ein späterer Tick pollt weiter); terminale
   * 4xx / fal-error / unerwarteter Status → failed (Treiber → ProviderJobFailedError).
   */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoInpaintJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown };
    // Handle ist persistiert (DB) → URLs erneut gegen den fal-Host pinnen (SSRF/Drift, D131-Codex-P1).
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      // 429/5xx sind transient — als "processing" melden, ein späterer Poll/Tick versucht erneut.
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal vace status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal vace: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        // Download passiert HIER (Output sind Bytes, nicht URL): im done-Zweig. Ein TRANSIENTER
        // Download-Flap (CDN/Result 429/5xx) NACH fertigem Job → 'processing' (Retry im nächsten
        // Poll/Tick), statt den fertigen Job hart zu killen (Codex-P1); terminale Fehler werfen.
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, handle.externalId, signal) };
        } catch (e) {
          if (e instanceof VaceDownloadTransientError) return { status: "processing" };
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal vace: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }
}
